<template>
  <div class="account-link-activation">
    <layout class="narrow" title="activation.title">
      <Transition name="fade">
        <FormError v-if="errorCode" :error-code="errorCode"></FormError>
      </Transition>
      <h4>{{ $t('activation.desc') }}</h4>
      <div class="input-code" :class="{ 'has-error': inputInvalid, 'has-value': inputCode.length > 0 }">
        <input
          ref="refInput1"
          v-model="inputCode"
          v-maska
          data-maska="****-****"
          :placeholder="$t('activation.input-placehold')"
        />
        <div v-show="inputCode.length > 0" class="clear-btn" @click="clear">
          <i-local-clear-light v-if="theme == 'light'" />
          <i-local-clear-dark v-else />
        </div>
        <p v-show="inputInvalid" class="input-message">{{ $t('error.form.invalid-code') }}</p>
      </div>

      <FormButton id="submit-code" data-cy="submit" :label="$t('verify')" :loading="buttonLoading" @click="checkCode" />
      <p v-if="errorCode || inputInvalid" v-t="'activation.get-new-code-desc'" class="new-code"></p>
    </layout>
  </div>
</template>

<script lang="ts" setup>
import { accountAPI } from '@/helpers/api'
import { computed, onMounted, ref } from 'vue'
import { vMaska } from 'maska'
import { useThemeStore } from '@/store/theme'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
const { theme } = storeToRefs(useThemeStore())

const inputCode = ref(''),
  errorCode = ref<number | string>(''),
  submitted = ref(false),
  buttonLoading = ref(false),
  refInput1 = ref<HTMLBaseElement>(),
  router = useRouter()

onMounted(() => {
  refInput1.value?.focus()
})

function clear() {
  inputCode.value = ''
  refInput1.value?.focus()
}

const inputInvalid = computed(() => submitted.value && inputCode.value.length < 9)

function checkCode() {
  if (inputInvalid.value) return
  submitted.value = true
  const code = inputCode.value
  errorCode.value = ''
  buttonLoading.value = true
  accountAPI
    .post('/no-auth/instruction/code/activate', {
      activationCode: code.replace('-', ''),
    })
    .then(({ data }) => {
      window.location.href = data.url
    })
    .catch((err: any) => {
      if (err.errorCode == 20187) {
        router.replace({ name: 'locked' })
      }
      errorCode.value = err.errorCode
      buttonLoading.value = false
    })
}
</script>
<style lang="less" scoped>
h4 {
  margin: 30px 0 45px 0;
}
.input-code {
  position: relative;
}

input {
  font-family: Consolas, monospace;
  width: calc(100% - 2px); // minus border size
  height: 50px;
  padding: 0;
  margin: 0;
  font-size: 16px;
  text-align: center;
  color: var(--text-c);
  background: var(--input-bg-c);
  border: 1px solid var(--border-c);
  outline: none !important;
  border-radius: 5px;
  // caret-color: transparent; // hide cursor
  transition: border-color 0.3s ease-in-out, color 0.3s ease-in-out;

  &:focus {
    border-color: var(--link-c);
  }
}
.clear-btn {
  position: absolute;
  top: 16px;
  right: 12px;
  cursor: pointer;
  opacity: 0.7;
  &:hover {
    opacity: 1;
  }
}

.has-value {
  input {
    text-indent: 16px;
    font-size: 20px;
    text-transform: uppercase;
    letter-spacing: 16px;
  }
}

.input-message {
  text-align: left;
}

@media only screen and (max-width: 479px) {
  .clear-btn {
    right: 4px;
  }
}
.has-error {
  input {
    border-color: var(--alert-c);
  }
  .input-message {
    color: var(--alert-c);
  }
}

#submit-code {
  margin-top: 40px;
}
.blocking-message {
  font-size: 16px;
  padding: 50px 10px 0;
  line-height: 25px;
  color: white;
}
.new-code {
  margin-top: 22px;
}
</style>
